import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Manufacturer } from "../../core/models/entities/Manufacturer";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { CreateManufacturerBody } from "../models/bodies/CreateManufacturerBody";
import { mapToManufacturer } from "../models/mappers/mapManufacturer";

export interface ManufacturerRepository {
  getManufacturers(orgId?: number): Promise<Manufacturer[]>;
  useManufacturers(options?: { enabled?: boolean }): Resource<Manufacturer[]>;
  prefetchManufacturers(): Promise<void>;
  useManufacturer(manufacturerId: number): Resource<Manufacturer>;
  createManufacturer(name: string): Promise<Manufacturer>;
}

export function useManufacturerRepository(): ManufacturerRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  async function invalidateQueries() {
    await queryClient.refetchQueries({
      queryKey: ["manufacturers"],
      type: "all",
      refetchPage: () => true,
    });
  }

  async function getManufacturers(orgId?: number): Promise<Manufacturer[]> {
    return apiClient.getAllManufacturers(orgId).then((x) => x.results);
  }

  function useManufacturers(options?: {
    enabled?: boolean;
  }): Resource<Manufacturer[]> {
    // Static master data: keep it cached past the 5 min GC default so
    // cross-screen hops hit cache. Paired with refetchOnMount: true (overriding
    // the global false) a stale cached list renders instantly and revalidates
    // in the background - freshness is never worse than today.
    const config = {
      staleTime: 600000,
      cacheTime: 30 * 60 * 1000,
      refetchOnMount: true,
      enabled: options?.enabled ?? true,
    };
    return useQuery(
      ["manufacturers"],
      () =>
        apiClient
          .getAllManufacturers()
          .then((data) => data.results.map(mapToManufacturer)),
      config
    );
  }

  // Cache-warming twin of useManufacturers (same key/queryFn/staleTime/cacheTime)
  // so e.g. the logo list can warm the design list's manufacturers gate. Fail-silent.
  function prefetchManufacturers(): Promise<void> {
    return queryClient.prefetchQuery(
      ["manufacturers"],
      () =>
        apiClient
          .getAllManufacturers()
          .then((data) => data.results.map(mapToManufacturer)),
      { staleTime: 600000, cacheTime: 30 * 60 * 1000 }
    );
  }

  function useManufacturer(manufacturerId: number): Resource<Manufacturer> {
    const config = { staleTime: 600000 };
    return useQuery(
      ["manufacturer", manufacturerId],
      () => apiClient.getManufacturer(manufacturerId).then(mapToManufacturer),
      config
    );
  }

  async function createManufacturer(name: string): Promise<Manufacturer> {
    const body: CreateManufacturerBody = {
      name: name,
    };
    const result = await apiClient.createManufacturer(body);
    await invalidateQueries();
    return mapToManufacturer(result);
  }

  return {
    getManufacturers,
    useManufacturers,
    prefetchManufacturers,
    useManufacturer,
    createManufacturer,
  };
}
